import { useMutation } from "react-query";
import { t } from "@/i18n-js/instance";
import { useToast } from "@circle-react/components/shared/uikit/ToastV2";
import { ToggleSwitchWithLabel } from "@circle-react/components/shared/uikit/ToggleSwitchWithLabel";
import type { ApiError } from "@circle-react/config/CustomErrors";
import { reactQueryPatch } from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import type { Post } from "@circle-react/types/Post";
import { Dropdown } from "@circle-react-shared/uikit/Dropdown";

export interface CloseCommentsActionProps {
  handleClose?: () => void;
  onChange?: (postData: Post, options: { refetch: boolean }) => void;
  post?: Post;
}

export const CloseCommentsAction = ({
  post,
  handleClose,
  onChange,
}: CloseCommentsActionProps) => {
  const isCommentsClosed = !!post?.is_comments_closed;
  const translationRoot = isCommentsClosed
    ? "post.open_comments"
    : "post.close_comments";
  const { success, error } = useToast();

  const { mutate: toggleCommentsClosed } = useMutation<Post, ApiError, void>(
    () =>
      reactQueryPatch(
        internalApi.posts.update({
          spaceId: post?.space_id,
          postSlug: post?.slug,
        }),
        {
          post: {
            space_id: post?.space_id,
            is_comments_closed: !isCommentsClosed,
          },
        },
      ),
    {
      onSuccess: postData => {
        onChange?.(postData, { refetch: false });
        success(t(`${translationRoot}.alerts.success`));
      },
      onError: () => {
        error(t(`${translationRoot}.alerts.failure`));
      },
      onSettled: handleClose,
    },
  );

  return (
    <Dropdown.ItemLink onClick={() => toggleCommentsClosed()}>
      <ToggleSwitchWithLabel
        label={t("post.action_menu.close_comments")}
        field="close_comments"
        value={isCommentsClosed}
        variant="small"
      />
    </Dropdown.ItemLink>
  );
};
